export type CsvDelimiter = ',' | ';';

/** Read a quoted CSV field starting after the opening quote; returns [value, nextIndex]. */
const readQuotedField = (line: string, start: number): [string, number] => {
  let j = start;
  let val = '';
  while (j < line.length) {
    if (line[j] === '"' && line[j + 1] === '"') {
      val += '"';
      j += 2;
    } else if (line[j] === '"') {
      j++;
      break;
    } else {
      val += line[j++];
    }
  }
  return [val, j];
};

/** Read an unquoted CSV field; returns [trimmed value, nextIndex]. */
const readUnquotedField = (
  line: string,
  start: number,
  delimiter: CsvDelimiter,
): [string, number] => {
  let j = start;
  while (j < line.length && line[j] !== delimiter) {
    j++;
  }
  return [line.slice(start, j).trim(), j < line.length ? j + 1 : j];
};

/** Split a CSV row, handling quoted fields. */
export const parseCSVLine = (
  line: string,
  delimiter: CsvDelimiter,
): string[] => {
  const result: string[] = [];
  let i = 0;
  while (i <= line.length) {
    if (i === line.length) {
      result.push('');
      break;
    }
    if (line[i] === '"') {
      const [val, next] = readQuotedField(line, i + 1);
      result.push(val);
      i = next;
      // Consume the separator after the closing quote only when it is the
      // active delimiter; a foreign char must stay so the count stays wrong
      // and delimiter detection can reject this delimiter.
      if (i < line.length && line[i] === delimiter) {
        i++;
      }
    } else {
      const [val, next] = readUnquotedField(line, i, delimiter);
      result.push(val);
      i = next;
    }
    if (i > line.length) {
      break;
    }
  }

  if (result.length > 0 && result.at(-1) === '' && !line.endsWith(delimiter)) {
    result.pop();
  }

  return result;
};

/** Delimiter used inside a cell for list values (opposite of the file row delimiter). */
const alternateDelimiter = (csvDelimiter: CsvDelimiter): CsvDelimiter =>
  csvDelimiter === ',' ? ';' : ',';

/**
 * Split a list cell into items. Row delimiter is detected at file level; list items
 * inside a cell may use either `,` or `;` — pick from cell content, not the file delimiter.
 */
export const splitCellList = (
  value: string,
  csvDelimiter: CsvDelimiter,
): string[] => {
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  const hasComma = trimmed.includes(',');
  const hasSemicolon = trimmed.includes(';');

  let listDelimiter: CsvDelimiter | null = null;
  if (hasSemicolon && !hasComma) {
    listDelimiter = ';';
  } else if (hasComma && !hasSemicolon) {
    listDelimiter = ',';
  } else if (hasSemicolon && hasComma) {
    listDelimiter = alternateDelimiter(csvDelimiter);
  } else {
    return [trimmed];
  }

  return trimmed
    .split(listDelimiter)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
};
